import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Trash2 } from "lucide-react";

import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { passwordOk } from "@/lib/format";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { BackHeader } from "@/components/back-header";
import { Button, Field } from "@/components/ui";

export default function AccountSecurity() {
  const navigate = useNavigate();
  const toast = useToast();
  const { signOut, refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delPw, setDelPw] = useState("");

  const changePassword = async (e) => {
    e.preventDefault();
    if (!passwordOk(next)) return toast("New password needs 8+ characters with letters and numbers", "error");
    if (next !== confirm) return toast("New passwords do not match", "error");
    setBusy("pw");
    try {
      const res = await api("/account/change-password", { method: "POST", body: { current_password: cur, new_password: next } });
      setToken(res.token);
      await refresh();
      setCur(""); setNext(""); setConfirm("");
      toast("Password updated. Other devices were signed out.", "success");
    } catch (err) { toast(err.message, "error"); } finally { setBusy(null); }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const data = await api("/account/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "lovenet-my-data.json";
      a.click();
      URL.revokeObjectURL(url);
      toast("Your data export is ready", "success");
    } catch (err) { toast(err.message, "error"); } finally { setBusy(null); }
  };

  const deleteAccount = async () => {
    setBusy("delete");
    try {
      await api("/account/delete", { method: "POST", body: { password: delPw } });
      setDeleteOpen(false);
      await signOut();
      toast("Your account has been deleted", "info");
      navigate("/landing", { replace: true });
    } catch (err) { toast(err.message, "error"); } finally { setBusy(null); }
  };

  const card = "mt-6 rounded-[20px] border bg-white p-5";
  return (
    <div data-testid="account-security-screen" className="mx-auto max-w-2xl">
      <BackHeader title="Account Security" />
      <form onSubmit={changePassword} className="rounded-[20px] border border-border bg-white p-5">
        <h2 className="mb-4 text-[24px]">Change password</h2>
        <Field testId="security-current-password-input" label="Current password" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
        <Field testId="security-new-password-input" label="New password" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} hint="At least 8 characters with letters and numbers" />
        <Field testId="security-confirm-password-input" label="Confirm new password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <Button type="submit" testId="security-change-password-button" title="Update password" loading={busy === "pw"} disabled={!cur || !next} />
      </form>
      <div className={`${card} border-border`}>
        <h2 className="text-[24px]">Download my data</h2>
        <p className="mb-4 mt-1 text-sm leading-6 text-muted-foreground">A JSON copy of your profile, preferences, requests, connections and notifications. Other members appear by display name only — never their contact details.</p>
        <Button testId="security-export-button" title="Download my data" variant="outline" icon={Download} loading={busy === "export"} onClick={exportData} />
      </div>
      <div className={`${card} border-[#9E3C3E]/40`}>
        <h2 className="text-[24px]">Delete account</h2>
        <p className="mb-4 mt-1 text-sm leading-6 text-muted-foreground">Your account is deactivated immediately and removed from discovery. We delete your photos and WhatsApp number, anonymise your profile, cancel pending requests and end all connections. Limited safety records may be retained for up to 12 months where required by law.</p>
        <Button testId="security-delete-account-button" title="Delete my account" variant="danger" icon={Trash2} onClick={() => setDeleteOpen(true)} />
      </div>
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete account?" testId="delete-account-sheet">
        <p className="mb-4 text-sm">This cannot be undone. Enter your password to confirm.</p>
        <Field testId="delete-password-input" label="Password" type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} />
        <div className="flex gap-3">
          <Button testId="delete-cancel-button" title="Cancel" variant="outline" className="flex-1" onClick={() => setDeleteOpen(false)} />
          <Button testId="delete-confirm-button" title="Delete" variant="danger" loading={busy === "delete"} disabled={!delPw} className="flex-1" onClick={deleteAccount} />
        </div>
      </Modal>
    </div>
  );
}
